<template>
  <CSidebar
    class="help-center-sidebar"
    overlaid
    :unfoldable="false"
    placement="end"
    :visible="isAsideVisible"
    :aria-hidden="isAsideVisible ? undefined : 'true'"
    :inert="isAsideVisible ? null : ''"
    color-scheme="light"
    size="lg"
    @visible-change="
      (value) => $store.commit('layout/set', ['asideShow', value])
    "
  >
    <button
      type="button"
      class="sidebar-close-btn"
      aria-label="Close help center"
      :tabindex="isAsideVisible ? 0 : -1"
      :aria-hidden="isAsideVisible ? undefined : 'true'"
      @click="closeAsideBar()"
    >
      <i class="ri-close-line" aria-hidden="true"></i>
    </button>
    <template v-if="isLoading">
      <div class="d-flex justify-content-center my-6">
        <CSpinner color="primary" />
      </div>
    </template>
    <template v-else>
      <div
        v-if="helpCenterContent"
        ref="helpContentRootRef"
        data-test-id="cy-help-center-sidebar"
        class="help-center-content pt-5"
        tabindex="-1"
        role="region"
        aria-label="Help center"
      >
        <CustomScrollbar>
          <div class="ps-4 pe-5" v-html="helpCenterContent"></div>
        </CustomScrollbar>
      </div>
    </template>
  </CSidebar>
</template>

<script>
import { reactive, computed, ref, watch, nextTick } from "vue"
import { useStore } from "vuex"
import { takeHelpCenterOpenerElement } from "@/store/modules/helpCenter"

export default {
  name: "TheAside",
  setup() {
    const store = useStore()
    const helpContentRootRef = ref(null)

    const state = reactive({
      scrollbarSettings: {
        suppressScrollY: false,
        suppressScrollX: true
      }
    })

    const helpCenterContent = computed(
      () => store.getters["helpCenter/getHelpCenterContent"]
    )
    const isAsideVisible = computed(() => store.state.layout.asideShow)

    const isLoading = computed(
      () => store?.state?.helpCenter?.isLoading || false
    )

    const focusableSelector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type='hidden'])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(",")

    const focusFirstHelpContent = async () => {
      if (
        !store.state.layout.asideShow ||
        isLoading.value ||
        !helpCenterContent.value
      ) {
        return
      }
      await nextTick()
      await nextTick()
      requestAnimationFrame(() => {
        const root = helpContentRootRef.value
        if (!root) return
        const first = root.querySelector(focusableSelector)
        if (first && typeof first.focus === "function") {
          first.focus()
        } else {
          root.focus()
        }
      })
    }

    const closeAsideBar = () => {
      store.commit("layout/toggle", "asideShow")
      store.commit("helpCenter/SET_CONTENT", "")
    }

    watch(
      () => store.state.layout.asideShow,
      async (isOpen, wasOpen) => {
        if (isOpen) {
          await focusFirstHelpContent()
          return
        }
        if (wasOpen) {
          await nextTick()
          const opener = takeHelpCenterOpenerElement()
          opener?.focus?.()
        }
      }
    )

    watch([isLoading, helpCenterContent], async () => {
      if (store.state.layout.asideShow) {
        await focusFirstHelpContent()
      }
    })

    return {
      state,
      isAsideVisible,
      helpCenterContent,
      closeAsideBar,
      isLoading,
      helpContentRootRef
    }
  }
}
</script>

<style lang="scss">
@import "src/assets/v3scss/_variables.scss";

.scroll-area {
  position: relative;
  margin: auto;
  width: 100%;
  height: calc(100vh - 200px);
}
.sidebar-close-btn {
  position: absolute;
  width: 64px;
  height: 64px;
  border: 0;
  right: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  .ri-close-line {
    font-size: 24px;
    /* WCAG 1.4.11: icon glyph vs light sidebar background (≥3:1); explicit token vs inherited `black`. */
    color: $gray-darken-50;
  }
  &:hover {
    background-color: transparent;
    border: none;
    cursor: pointer;
  }
  &:focus {
    outline: none;
    box-shadow: none;
  }
  &:focus-visible {
    /* Match CustomModal / shared focus ring — 2172097 */
    outline: 2px solid $outline-color !important;
    outline-offset: $outline-offset;
    border-radius: 2px;
  }
}
</style>
